import React, { useEffect, useState } from "react";
import { api, openAuthedFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Briefcase, Users, Clock, CheckCircle2, XCircle, FileText, Eye, Download } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABELS = {
  pending: "En attente",
  in_review: "En examen",
  docs_required: "Documents requis",
  approved: "Accepté",
  rejected: "Refusé",
};
const STATUS_COLORS = {
  pending: "bg-slate-100 text-slate-700",
  in_review: "bg-blue-100 text-blue-800",
  docs_required: "bg-orange-100 text-orange-800",
  approved: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
};
const DOC_LABELS = {
  diplome: "Diplôme", cni: "CNI/Passeport", cv: "CV",
  lettre_motivation: "Lettre motivation", photo1: "Photo 1", photo2: "Photo 2",
};

export default function CandidaturesPage() {
  const [dash, setDash] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [decision, setDecision] = useState({ open: false, action: "approve", notes: "" });

  const load = async () => {
    try {
      const [{ data: d }, { data: cs }] = await Promise.all([
        api.get("/recruitment/dashboard"),
        api.get("/recruitment/candidates", { params: filter !== "all" ? { status: filter } : {} }),
      ]);
      setDash(d); setCandidates(cs);
    } catch { toast.error("Erreur de chargement"); }
  };
  useEffect(() => { load(); }, [filter]);

  const view = async (id) => {
    const { data } = await api.get(`/recruitment/candidates/${id}`);
    setSelected(data);
  };

  const submitDecision = async () => {
    try {
      const params = new URLSearchParams({ decision: decision.action, notes: decision.notes });
      const { data } = await api.post(`/recruitment/candidates/${selected.id}/decision?${params.toString()}`);
      toast.success(`Décision enregistrée. ${data.account ? "Compte enseignant créé et email envoyé." : "Candidat notifié par email."}`);
      if (data.account) {
        toast.info(`Mot de passe temporaire : ${data.account.temp_password}`, { duration: 15000 });
      }
      setDecision({ open: false, action: "approve", notes: "" });
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="space-y-6" data-testid="candidatures-page">
      <div>
        <div className="chip mb-2"><Briefcase className="h-3 w-3" /><span>RECRUTEMENT — CANDIDATURES</span></div>
        <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Candidatures enseignants</h1>
        <p className="text-slate-500 mt-1">Consultez, évaluez et validez les candidatures externes.</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI label="Total" value={dash?.total || 0} icon={Users} color="text-[#1E3A8A]" />
        <KPI label="En attente" value={dash?.pending || 0} icon={Clock} color="text-slate-600" />
        <KPI label="En examen" value={dash?.in_review || 0} icon={FileText} color="text-blue-600" />
        <KPI label="Acceptés" value={dash?.approved || 0} icon={CheckCircle2} color="text-[#166534]" />
        <KPI label="Refusés" value={dash?.rejected || 0} icon={XCircle} color="text-red-600" />
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-56" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les candidatures</SelectItem>
            {Object.entries(STATUS_LABELS).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* List */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Candidat</th>
                <th className="px-4 py-3">Matières</th>
                <th className="px-4 py-3">Expérience</th>
                <th className="px-4 py-3">Documents</th>
                <th className="px-4 py-3">Statut</th>
                <th className="px-4 py-3">Soumis</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {candidates.map(c => (
                <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`cand-${c.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-[#1E3A8A]">{c.first_name} {c.last_name}</div>
                    <div className="text-xs text-slate-500">{c.email}</div>
                  </td>
                  <td className="px-4 py-3 max-w-xs text-slate-700">{c.subjects_taught || "—"}</td>
                  <td className="px-4 py-3 tabular-nums">{c.experience_years || 0} an(s)</td>
                  <td className="px-4 py-3 tabular-nums">{Object.keys(c.documents || {}).length}/6</td>
                  <td className="px-4 py-3"><span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${STATUS_COLORS[c.status]}`}>{STATUS_LABELS[c.status]}</span></td>
                  <td className="px-4 py-3 text-xs text-slate-500">{new Date(c.submitted_at).toLocaleDateString("fr-FR")}</td>
                  <td className="px-4 py-3"><Button size="sm" variant="outline" onClick={() => view(c.id)} data-testid={`view-${c.id}`}><Eye className="h-4 w-4" /></Button></td>
                </tr>
              ))}
              {candidates.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400">Aucune candidature.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader><DialogTitle>{selected.first_name} {selected.last_name} — {STATUS_LABELS[selected.status]}</DialogTitle></DialogHeader>
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3 bg-slate-50 p-4 rounded-lg">
                  <div><div className="text-xs uppercase text-slate-500 font-bold">Sexe</div><div>{selected.gender === "F" ? "Femme" : "Homme"}</div></div>
                  <div><div className="text-xs uppercase text-slate-500 font-bold">Naissance</div><div>{selected.birth_date}</div></div>
                  <div><div className="text-xs uppercase text-slate-500 font-bold">Email</div><div>{selected.email}</div></div>
                  <div><div className="text-xs uppercase text-slate-500 font-bold">Téléphone</div><div>{selected.phone}</div></div>
                  <div className="col-span-2"><div className="text-xs uppercase text-slate-500 font-bold">Adresse</div><div>{selected.address || "—"}</div></div>
                  <div><div className="text-xs uppercase text-slate-500 font-bold">Matières</div><div>{selected.subjects_taught || "—"}</div></div>
                  <div><div className="text-xs uppercase text-slate-500 font-bold">Expérience</div><div>{selected.experience_years} an(s)</div></div>
                  <div className="col-span-2"><div className="text-xs uppercase text-slate-500 font-bold">Diplôme</div><div>{selected.highest_degree || "—"}</div></div>
                </div>
                {selected.cover_letter_text && (
                  <div><div className="text-xs uppercase text-slate-500 font-bold mb-1">Message de motivation</div>
                    <div className="p-3 bg-slate-50 rounded whitespace-pre-wrap">{selected.cover_letter_text}</div></div>
                )}
                <div>
                  <div className="text-xs uppercase text-slate-500 font-bold mb-2">Documents ({Object.keys(selected.documents || {}).length}/6)</div>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(DOC_LABELS).map(([k, label]) => {
                      const d = selected.documents?.[k];
                      return (
                        <div key={k} className={`border rounded p-2 text-xs flex items-center justify-between ${d ? "border-green-300 bg-green-50" : "border-slate-200 bg-slate-50"}`}>
                          <span className="font-semibold">{label}</span>
                          {d ? <button type="button" onClick={() => openAuthedFile(`/recruitment/applications/${selected.id}/documents/${k}/file`).catch(() => toast.error("Fichier indisponible"))} className="text-[#1E3A8A] font-bold hover:underline" data-testid={`view-doc-${k}`}>Voir</button> : <span className="text-slate-400">Absent</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
                {selected.director_notes && (
                  <div><div className="text-xs uppercase text-slate-500 font-bold mb-1">Notes précédentes</div>
                    <div className="p-3 bg-yellow-50 rounded text-sm">{selected.director_notes}</div></div>
                )}
              </div>
              {selected.status !== "approved" && selected.status !== "rejected" && (
                <DialogFooter className="border-t border-slate-200 pt-4">
                  <Button variant="outline" onClick={() => setDecision({ open: true, action: "request_docs", notes: "" })} data-testid="btn-request-docs">Demander compléments</Button>
                  <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={() => setDecision({ open: true, action: "reject", notes: "" })} data-testid="btn-reject"><XCircle className="h-4 w-4 mr-1" />Refuser</Button>
                  <Button className="bg-[#166534] hover:bg-[#14532d] text-white" onClick={() => setDecision({ open: true, action: "approve", notes: "" })} data-testid="btn-approve"><CheckCircle2 className="h-4 w-4 mr-1" />Accepter</Button>
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Decision confirmation dialog */}
      <Dialog open={decision.open} onOpenChange={(o) => !o && setDecision({...decision, open: false})}>
        <DialogContent>
          <DialogHeader><DialogTitle>Confirmer la décision : {decision.action === "approve" ? "Acceptation" : decision.action === "reject" ? "Refus" : "Demande de compléments"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-slate-600">
              {decision.action === "approve" && "Un compte enseignant sera créé automatiquement avec un mot de passe temporaire. Le candidat recevra ses identifiants par email."}
              {decision.action === "reject" && "Le candidat sera notifié du refus par email."}
              {decision.action === "request_docs" && "Le candidat recevra un email lui demandant des documents complémentaires."}
            </div>
            <div>
              <label className="text-sm font-semibold">Notes / message au candidat</label>
              <Textarea rows={4} value={decision.notes} onChange={e => setDecision({...decision, notes: e.target.value})} data-testid="decision-notes" placeholder="Facultatif : ajoutez un message personnalisé..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecision({...decision, open: false})}>Annuler</Button>
            <Button onClick={submitDecision} className={decision.action === "approve" ? "bg-[#166534] text-white" : decision.action === "reject" ? "bg-red-600 text-white" : "bg-orange-600 text-white"} data-testid="confirm-decision">
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KPI({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</span>
        <Icon className={`h-4 w-4 ${color}`} />
      </div>
      <div className={`text-2xl font-extrabold ${color} tabular-nums`}>{value}</div>
    </div>
  );
}
